import { normalizeBarcodeValue } from '@/lib/barcodeScan';

export interface ProductBarcodeFields {
  id: string;
  sku?: string | null;
  barcode?: string | null;
  unitBarcode?: string | null;
  caseBarcode?: string | null;
}

function barcodeCandidates(value: string | null | undefined): string[] {
  if (!value?.trim()) return [];
  const trimmed = value.trim();
  const normalized = normalizeBarcodeValue(trimmed);
  const upper = trimmed.toUpperCase();
  const normalizedUpper = normalized.toUpperCase();
  return Array.from(new Set([trimmed, normalized, upper, normalizedUpper]));
}

export function matchProductByBarcode<T extends ProductBarcodeFields>(
  products: T[],
  rawBarcode: string
): T | undefined {
  const scanned = barcodeCandidates(rawBarcode);
  if (scanned.length === 0) return undefined;

  for (const product of products) {
    const productCodes = [
      product.unitBarcode,
      product.barcode,
      product.caseBarcode,
      product.sku,
    ].flatMap((code) => barcodeCandidates(code));

    if (productCodes.some((code) => scanned.includes(code))) {
      return product;
    }
  }

  return undefined;
}

export type ProductSearchFields = ProductBarcodeFields & {
  name: string;
  sku: string;
};

function codeIncludesQuery(code: string | null | undefined, query: string, normalizedQuery: string): boolean {
  if (!code?.trim()) return false;
  const trimmed = code.trim();
  if (trimmed.toLowerCase().includes(query)) return true;
  return normalizeBarcodeValue(trimmed).toLowerCase().includes(normalizedQuery);
}

/** Filter products by name, SKU, or barcode (partial or exact). */
export function filterProductsByQuery<T extends ProductSearchFields>(
  products: T[],
  rawQuery: string
): T[] {
  const query = rawQuery.trim();
  if (!query) return products;

  const exactBarcodeMatch = matchProductByBarcode(products, query);
  if (exactBarcodeMatch) return [exactBarcodeMatch];

  const lower = query.toLowerCase();
  const normalizedQuery = normalizeBarcodeValue(query).toLowerCase();

  return products.filter((product) => {
    if (product.name.toLowerCase().includes(lower)) return true;
    if (product.sku.toLowerCase().includes(lower)) return true;
    return (
      codeIncludesQuery(product.barcode, lower, normalizedQuery) ||
      codeIncludesQuery(product.unitBarcode, lower, normalizedQuery) ||
      codeIncludesQuery(product.caseBarcode, lower, normalizedQuery)
    );
  });
}
